import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { X, RotateCw, Crop as CropIcon } from "lucide-react";

/**
 * <ImageCropperModal />
 *
 * Full-screen modal that crops the given image (data URI or absolute https URL)
 * to a fixed aspect ratio and returns the result as a JPEG data URI via onSave.
 * Used inside ImageUploadField so admins can dial a product photo into the
 * warranty card's 1:1 photo frame with no dead-space or awkward cropping.
 *
 * Props:
 *   src          — data URI or https URL to crop
 *   aspect       — width / height ratio (default 1 for square)
 *   outputSize   — output side length in pixels (default 800). Square only.
 *   onCancel     — called when user dismisses without saving
 *   onSave       — called with a JPEG data URI string of the cropped area
 */
const createImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.setAttribute("crossOrigin", "anonymous"); // avoid canvas tainting from CDN URLs
  img.onload = () => resolve(img);
  img.onerror = (e) => reject(e);
  img.src = url;
});

const cropToDataUri = async (src, cropPixels, rotation, outputSize) => {
  const image = await createImage(src);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // If rotated 90 or 270 the effective crop bounding box swaps w/h
  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  const bbW = image.width * cos + image.height * sin;
  const bbH = image.width * sin + image.height * cos;

  // Draw the whole rotated image onto a temporary canvas
  const tmp = document.createElement("canvas");
  tmp.width = bbW;
  tmp.height = bbH;
  const tctx = tmp.getContext("2d");
  tctx.translate(bbW / 2, bbH / 2);
  tctx.rotate(rad);
  tctx.drawImage(image, -image.width / 2, -image.height / 2);

  // Now crop out cropPixels from that rotated image
  canvas.width = outputSize;
  canvas.height = outputSize;
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, outputSize, outputSize);
  ctx.drawImage(
    tmp,
    cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
    0, 0, outputSize, outputSize,
  );
  return canvas.toDataURL("image/jpeg", 0.92);
};

const ImageCropperModal = ({
  src,
  aspect = 1,
  outputSize = 800,
  onCancel,
  onSave,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pixels, setPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setPixels(areaPixels);
  }, []);

  const handleSave = async () => {
    if (!pixels) return;
    setSaving(true);
    try {
      const dataUri = await cropToDataUri(src, pixels, rotation, outputSize);
      onSave(dataUri);
    } catch (e) {
      // Fallback: return the original if canvas is tainted by a CDN URL that
      // doesn't send CORS headers. The admin can still hand-crop upstream.
      setSaving(false);
      alert("Could not crop this image (likely a cross-origin CDN URL without CORS). Please upload the photo directly from your device instead.");
    }
  };

  return (
    <div
      data-testid="image-cropper-modal"
      className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-sm flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <CropIcon className="text-gold" size={18} />
          <div className="font-accent uppercase tracking-[0.24em] text-xs text-gold">Fit photo to warranty card</div>
        </div>
        <button data-testid="cropper-close" onClick={onCancel} className="text-white/60 hover:text-white">
          <X size={22} />
        </button>
      </div>

      {/* Cropper canvas */}
      <div className="relative flex-1 bg-onyx">
        <Cropper
          image={src}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={aspect}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
          objectFit="contain"
          showGrid={true}
          cropShape="rect"
          style={{ containerStyle: { background: "#0A0A0A" } }}
        />
      </div>

      {/* Controls */}
      <div className="px-6 py-5 border-t border-white/10 bg-noir">
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="flex items-center gap-4">
            <span className="font-accent text-[10px] tracking-[0.24em] uppercase text-white/50 w-16">Zoom</span>
            <input
              data-testid="cropper-zoom"
              type="range"
              min={1} max={4} step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-gold"
            />
            <span className="text-white/50 text-xs w-12 text-right">{zoom.toFixed(2)}x</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-accent text-[10px] tracking-[0.24em] uppercase text-white/50 w-16">Rotate</span>
            <input
              data-testid="cropper-rotate"
              type="range"
              min={0} max={360} step={1}
              value={rotation}
              onChange={(e) => setRotation(Number(e.target.value))}
              className="flex-1 accent-gold"
            />
            <button
              data-testid="cropper-rotate90"
              onClick={() => setRotation((r) => (r + 90) % 360)}
              className="flex items-center gap-1 text-white/70 hover:text-gold border border-white/20 hover:border-gold px-3 py-1.5 font-accent text-[10px] uppercase tracking-[0.18em]"
            >
              <RotateCw size={12} /> 90°
            </button>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button
              data-testid="cropper-cancel"
              onClick={onCancel}
              className="border border-white/25 text-white/80 hover:text-white hover:border-white font-accent uppercase tracking-[0.24em] text-xs px-6 py-3"
            >
              Cancel
            </button>
            <button
              data-testid="cropper-save"
              onClick={handleSave}
              disabled={!pixels || saving}
              className="bg-gold text-black font-accent uppercase tracking-[0.24em] text-xs px-7 py-3 hover:bg-ivory disabled:opacity-50"
            >
              {saving ? "Cropping…" : "Apply crop"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropperModal;
